import { z } from 'zod';
// Shared by both providers: endedAt is Unix epoch milliseconds, never ISO text.
export const AuthoritativeCloseSchema = z.object({
    type: z.literal('close_acknowledged'),
    reason: z.enum(['owner_closed', 'runtime_completed']),
    endedAt: z.number().int().positive(),
}).strict();
export const GEMINI_MAX_FRAME_BYTES = 48 * 1024;
export const GEMINI_MAX_INPUT_PCM_BYTES = 6_400;
export const GEMINI_MAX_OUTPUT_PCM_BYTES = 24_000;
export const GEMINI_MAX_TRANSCRIPT_CHARACTERS = 8_000;
const sessionId = z.string().uuid();
const controlToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
const epoch = z.number().int().min(1).max(Number.MAX_SAFE_INTEGER);
const sequence = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const base64Alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
// Validate the bounded encoding without allocating decoded audio or using Node APIs.
function pcmBase64(maxBytes) {
    const maxCharacters = 4 * Math.ceil(maxBytes / 3);
    return z.string().min(4).max(maxCharacters).refine((data) => {
        if (data.length < 4 || data.length > maxCharacters || data.length % 4 !== 0)
            return false;
        if (!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(data))
            return false;
        const padding = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0;
        const bytes = data.length / 4 * 3 - padding;
        if (bytes < 2 || bytes > maxBytes || bytes % 2 !== 0)
            return false;
        // The unused bits of the final sextet must be zero for a canonical encoding.
        if (padding === 2)
            return (base64Alphabet.indexOf(data[data.length - 3]) & 15) === 0;
        if (padding === 1)
            return (base64Alphabet.indexOf(data[data.length - 2]) & 3) === 0;
        return true;
    }, 'Expected bounded canonical base64 PCM16');
}
export const GeminiSessionIssuanceSchema = z.object({
    provider: z.literal('gemini'),
    sessionId,
    controlToken,
    model: z.enum(['gemini-3.8-live', 'gemini-3.8-live-extended-thinking']),
    voice: z.literal('Kore'),
    websocketPath: z.literal('/ws/gemini-live'),
}).strict();
export const GeminiAttachMessageSchema = z.object({ type: z.literal('attach'), sessionId, controlToken }).strict();
const normalisedText = (max) => z.string().trim().min(1).max(max)
    .transform((value) => value.replace(/\s+/g, ' '));
export const GeminiContactDetailsSchema = z.object({
    name: normalisedText(120),
    email: z.string().trim().max(254).email().transform((value) => value.toLowerCase()),
    phone: z.preprocess(value => typeof value === 'string' && !value.trim() ? undefined : value, normalisedText(40).refine(value => /^[+()\d .-]+$/.test(value)
        && value.replace(/\D/g, '').length >= 7 && value.replace(/\D/g, '').length <= 15, 'Invalid phone').optional()),
    organisation: normalisedText(160),
}).strict();
export const GeminiCallerDetailsMessageSchema = z.object({
    type: z.literal('caller_details_provided'),
    revision: sequence,
    consent: z.literal(true),
    data: GeminiContactDetailsSchema,
}).strict();
export const GeminiContactEditingMessageSchema = z.object({
    type: z.literal('contact_details_editing'), revision: sequence,
}).strict();
export const GeminiMediaReadyMessageSchema = z.object({ type: z.literal('media_ready'), transportEpoch: epoch }).strict();
export const GeminiAudioInputMessageSchema = z.object({
    type: z.literal('audio_input'), transportEpoch: epoch, sequence, data: pcmBase64(GEMINI_MAX_INPUT_PCM_BYTES),
}).strict();
export const GeminiAudioStreamEndMessageSchema = z.object({
    type: z.literal('audio_stream_end'), transportEpoch: epoch,
}).strict();
export const GeminiPlayoutStartedMessageSchema = z.object({
    type: z.literal('playout_started'), transportEpoch: epoch, playbackEpoch: epoch, sequence,
}).strict();
export const GeminiPlayoutStoppedMessageSchema = z.object({
    type: z.literal('playout_stopped'), transportEpoch: epoch, playbackEpoch: epoch, throughSequence: sequence,
}).strict();
export const GeminiCloseMessageSchema = z.object({
    type: z.literal('close'), reason: z.string().trim().max(160).optional(),
}).strict();
export const GeminiAttachedClientMessageSchema = z.discriminatedUnion('type', [
    GeminiCallerDetailsMessageSchema, GeminiContactEditingMessageSchema, GeminiMediaReadyMessageSchema, GeminiAudioInputMessageSchema,
    GeminiAudioStreamEndMessageSchema, GeminiPlayoutStartedMessageSchema, GeminiPlayoutStoppedMessageSchema, GeminiCloseMessageSchema,
]);
export const GeminiClientMessageSchema = z.discriminatedUnion('type', [
    GeminiAttachMessageSchema, ...GeminiAttachedClientMessageSchema.options,
]);
export const GeminiSessionReadyMessageSchema = z.object({
    type: z.literal('session_ready'), sessionId, transportEpoch: z.literal(1),
}).strict();
export const GeminiTransportStateMessageSchema = z.object({
    type: z.literal('transport_state'), state: z.enum(['reconnecting', 'connected']), transportEpoch: epoch,
}).strict();
export const GeminiAudioOutputMessageSchema = z.object({
    type: z.literal('audio_output'), transportEpoch: epoch, playbackEpoch: epoch, sequence,
    data: pcmBase64(GEMINI_MAX_OUTPUT_PCM_BYTES),
}).strict();
export const GeminiTranscriptMessageSchema = z.object({
    type: z.literal('transcript'), role: z.enum(['user', 'assistant']),
    text: z.string().max(GEMINI_MAX_TRANSCRIPT_CHARACTERS), finished: z.boolean(),
}).strict();
export const GeminiInterruptedMessageSchema = z.object({
    type: z.literal('interrupted'), transportEpoch: epoch, playbackEpoch: epoch,
}).strict();
export const GeminiTurnCompleteMessageSchema = z.object({ type: z.literal('turn_complete') }).strict();
export const GeminiInteractionStatusMessageSchema = z.object({
    type: z.literal('interaction_status'), status: z.enum(['IN_PROGRESS', 'IDLE']),
}).strict();
// Consumers compose existing strict UI, error, identity, usage and close-ack schemas.
export const GeminiServerMessageSchema = z.discriminatedUnion('type', [
    GeminiSessionReadyMessageSchema, GeminiTransportStateMessageSchema, GeminiAudioOutputMessageSchema,
    GeminiTranscriptMessageSchema, GeminiInterruptedMessageSchema, GeminiTurnCompleteMessageSchema,
    GeminiInteractionStatusMessageSchema,
]);
//# sourceMappingURL=live-session.js.map